/**
 * Based on code from:
 * https://github.com/Kitware/VolView/blob/main/src/vtk/CrosshairsWidget/state.ts
 */

import vtkStateBuilder from "vtk.js/Sources/Widgets/Core/StateBuilder";
import { SlicingMode } from "vtk.js/Sources/Rendering/Core/ImageMapper/Constants";

/**
 * Axis-aligned bounds: [minX, maxX, minY, maxY, minZ, maxZ].
 * @typedef {[number, number, number, number, number, number]} Bounds
 */

/**
 * 3D vector [x, y, z].
 * @typedef {[number, number, number]} Vector3
 */

/**
 * 4x4 matrix as 16 numbers (row-major as used by gl-matrix's mat4).
 * @typedef {number[]} Mat4
 */

/**
 * @typedef {Object} CrosshairsHandleWidgetState
 * @property {(origin: Vector3|null) => boolean} setOrigin
 * @property {() => (Vector3|null)} getOrigin
 * @property {(scale: number) => boolean} setScale1
 * @property {() => number} getScale1
 * @property {(visible: boolean) => boolean} setVisible
 * @property {() => boolean} getVisible
 * @property {(bounds: Bounds) => boolean} setBounds
 * @property {() => Bounds} getBounds
 */

/**
 * @typedef {Object} CrosshairsWidgetState
 * @property {(enabled: boolean) => boolean} setEnabled
 * @property {() => boolean} getEnabled
 * @property {(dragging: boolean) => boolean} setDragging
 * @property {() => boolean} getDragging
 * @property {(m: Mat4) => boolean} setIndexToWorld
 * @property {() => Mat4} getIndexToWorld
 * @property {(m: Mat4) => boolean} setWorldToIndex
 * @property {() => Mat4} getWorldToIndex
 * @property {() => CrosshairsHandleWidgetState} getHandle
 */

/**
 * Build the widget state.
 * @returns {import('vtk.js/Sources/Widgets/Core/StateBuilder').IStateBuilder}
 */
export default function generateState() {
    return vtkStateBuilder
        .createBuilder()
        .addField({
            name: "enabled",
            initialValue: false,
        })
        .addField({
            name: "dragging",
            initialValue: false,
        })
        .addField({
            name: "indexToWorld",
            initialValue: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        })
        .addField({
            name: "worldToIndex",
            initialValue: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        })
        .addStateFromMixin({
            labels: ["handle"],
            mixins: ["origin", "bounds"],
            name: "handle",
            initialValues: {
                origin: null,
            },
        })
        .build();
}